import apiClient from './apiClient';
import { User } from './authService';
import authService from './authService';

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  profileImage: string;
  jobTitle: string;
  careerLevel: string;
  resume: string;
  skills: string[];
  preferredCompanies: string[];
  targetRoles: string[];
  industry: string;
  location: string;
}

export interface SubscriptionDetails {
  plan: string;
  status: 'active' | 'inactive' | 'pending' | 'canceled';
  expiry: string;
  remainingInterviews: number;
  remainingMinutes: number;
  features: string[];
  canUpgrade: boolean;
  nextBillingDate?: string;
  price?: number;
}

export interface SettingsPreferences {
  emailNotifications: boolean;
  darkMode: boolean;
  language: string;
  videoQuality: 'low' | 'medium' | 'high';
  audioEnabled: boolean;
  videoEnabled: boolean;
  interviewLanguage: string;
  timezone: string;
}

// Interface for profile update data
export interface UpdateProfileData {
  name?: string;
  email?: string;
  phone?: string;
  position?: string;
  profilePicture?: File | string;
  // Add any additional fields that can be updated
}

// Interface for the profile update response
export interface UpdateProfileResponse {
  success: boolean;
  message: string;
  data?: User;
  statusCode?: number;
}

class UserService {
  /**
   * Get the current authenticated user's detailed profile from the backend
   * Handles the nested API response structure:
   * {
   *   statusCode: 200,
   *   message: "Operation successful",
   *   data: {
   *     success: true,
   *     statusCode: 200,
   *     message: "Operation successful",
   *     data: { ... user data ... }
   *   }
   * }
   * 
   * @param forceRefresh Force a fresh fetch even if cached data is available
   * @returns User profile data including ID, email, name, etc.
   */
  async getCurrentUser(forceRefresh = false): Promise<User> {
    try {
      // Delegate to authService.getCurrentUser which has robust caching and request deduplication
      console.log(`UserService: getCurrentUser called, forceRefresh=${forceRefresh}`);
      
      // Use authService which already handles caching, token refresh, and deduplication
      const userData = await authService.getCurrentUser(forceRefresh);
      
      if (!userData) {
        console.error("UserService: authService returned null user data");
        throw new Error("Failed to get user data");
      }
      
      console.log("UserService: User data obtained from authService:", {
        userId: userData._id,
        hasProfilePicture: !!userData.profilePicture
      });
      
      return userData;
    } catch (error) {
      console.error("UserService: Error fetching user profile:", error);
      throw error;
    }
  }

  /**
   * Get public app information that doesn't require authentication
   * @returns Public app configuration
   */
  async getPublicConfig(): Promise<any> {
    // This is a public endpoint that doesn't require authentication
    return apiClient.get<any>('/config/public', undefined, false);
  }

  /**
   * Get user profile details with additional information
   * @returns Extended user profile data
   */
  async getUserProfile(): Promise<UserProfile> {
    return apiClient.get<UserProfile>('/users/profile');
  }


  /**
   * Get user subscription details
   * @returns Subscription details
   */
  async getSubscription(): Promise<SubscriptionDetails> {
    return apiClient.get<SubscriptionDetails>('/users/subscription');
  }

  /**
   * Subscribe to a plan
   * @param planId Plan identifier
   * @returns Updated subscription
   */
  async subscribeToPlan(planId: string): Promise<SubscriptionDetails> {
    return apiClient.post<SubscriptionDetails>('/users/subscription', { planId });
  }

  /**
   * Cancel subscription
   * @returns Updated subscription
   */
  async cancelSubscription(): Promise<SubscriptionDetails> {
    return apiClient.delete<SubscriptionDetails>('/users/subscription');
  }
}

// Export service as singleton
export const userService = new UserService();
export default userService; 